import React, { useState } from "react";
import styled from "styled-components";
import { Button } from "antd";
import { HeartOutlined, StarFilled } from "@ant-design/icons";
import { discoverPackages, discoverImages } from "./Common";

const Discover = styled.div`
  padding-top: 7rem;
  padding-left: 8rem;
  padding-right: 8rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SmallHeader = styled.h2`
  text-align: left;
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 100%;
`;

const DiscoverContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DiscoverRow = styled.div`
  display: contents;
`;

const DiscoverCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DiscoverCard = styled.div`
  border-radius: 16px;
  height: 250px;
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
    border-radius: 16px;
  }
`;

const DiscoverCardTop = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  position: relative;
  z-index: 2;
`;

const DiscoverCardBottom = styled.div`
  padding: 0 0.5rem;
  color: #333;
`;

const LoadMoreButton = styled(Button)`
  background: #1890ff;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0 24px;
  height: 40px;
  font-weight: 500;

  &:hover {
    background: #40a9ff;
    color: white;
  }
`;

const LikeButton = styled(Button)`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(240, 240, 240, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #333;

  .anticon {
    color: #333;
    font-size: 16px;
  }
`;

const PackageTitle = styled.h3`
  color: #333;
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px 0;
`;

const Location = styled.span`
  color: #666;
  font-size: 14px;
  font-weight: 500;
  display: block;
  margin-bottom: 8px;
`;

const PackagePriceRating = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PackagePrice = styled.span`
  color: #1890ff;
  font-size: 16px;
  font-weight: 700;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RatingNumber = styled.span`
  color: #333;
  font-size: 14px;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const DiscoverSection: React.FC = () => {
  const [visibleRows, setVisibleRows] = useState(2); // Show 2 rows initially (8 cards)
  const cardsPerRow = 4;

  const loadMore = () => {
    setVisibleRows(prev => prev + 1); // Add one more row (4 more cards)
  };

  // Calculate total cards to display
  const totalCardsToShow = visibleRows * cardsPerRow;
  const hasMoreCards = totalCardsToShow < discoverPackages.length;

  return (
    <Discover>
      <SmallHeader>Discover Amazing Deals for a Memorable Getaway.</SmallHeader>
      <DiscoverContainer>
        {Array.from({ length: visibleRows }).map((_, rowIndex) => (
          <DiscoverRow key={rowIndex}>
            {discoverPackages.slice(rowIndex * cardsPerRow, (rowIndex + 1) * cardsPerRow).map((pkg, index) => {
              const cardIndex = rowIndex * cardsPerRow + index;
              return (
                <DiscoverCardWrapper key={cardIndex}>
                  <DiscoverCard
                    style={{ backgroundImage: `url(${discoverImages[cardIndex % discoverImages.length]})` }}
                  >
                    <DiscoverCardTop>
                      <LikeButton icon={<HeartOutlined />} />
                    </DiscoverCardTop>
                  </DiscoverCard>
                  <DiscoverCardBottom>
                    <PackageTitle>{pkg.package}</PackageTitle>
                    <Location>{pkg.location}</Location>
                    <PackagePriceRating>
                      <PackagePrice>${pkg.price}</PackagePrice>
                      <Rating>
                        <StarFilled style={{ color: "#FFD700" }} />
                        <RatingNumber>{pkg.rating}</RatingNumber>
                      </Rating>
                    </PackagePriceRating>
                  </DiscoverCardBottom>
                </DiscoverCardWrapper>
              );
            })}
          </DiscoverRow>
        ))}
      </DiscoverContainer>
      {hasMoreCards && (
        <ButtonContainer>
          <LoadMoreButton onClick={loadMore}>See More Packages</LoadMoreButton>
        </ButtonContainer>
      )}
    </Discover>
  );
};

export default DiscoverSection;
